import { View, Text, Pressable, Image } from "react-native";
import { ChevronRight } from "lucide-react-native";
import Animated, { FadeIn } from "react-native-reanimated";
import { EMBER } from "../../../onboarding/theme";
import { CATEGORY_CONFIG } from "./searchTypes";
import type { SearchItem } from "./searchTypes";
import {
  pressLabel,
  usePressCapture,
} from "@src/packages/analytics/usePressCapture";
import { semanticColors, withAlpha } from "@louise/theme/tokens";

type Props = {
  item: SearchItem;
  index: number;
  onPress: (item: SearchItem) => void;
};

function ResultIcon({ item }: { item: SearchItem }) {
  const config = CATEGORY_CONFIG[item.category];
  const Icon = config.icon;
  const img = item.image;

  // Image from require() (e.g. supplement bottles)
  if (img?.type === "require") {
    return (
      <View
        style={{
          width: 36,
          height: 36,
          borderRadius: 10,
          backgroundColor: withAlpha(config.color, 0.06),
          alignItems: "center",
          justifyContent: "center",
          overflow: "hidden",
        }}
      >
        <Image
          source={img.source}
          style={{ width: 28, height: 28 }}
          resizeMode="contain"
        />
      </View>
    );
  }

  // Image from URI (e.g. avatar URL)
  if (img?.type === "uri") {
    return (
      <Image
        source={{ uri: img.uri }}
        style={{
          width: 36,
          height: 36,
          borderRadius: 18,
          backgroundColor: withAlpha(config.color, 0.06),
        }}
      />
    );
  }

  // Initials avatar (e.g. care team members)
  if (img?.type === "initials") {
    return (
      <View
        style={{
          width: 36,
          height: 36,
          borderRadius: 18,
          backgroundColor: withAlpha(img.bgColor, 0.09),
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Text
          className="font-dm-sans"
          style={{
            fontSize: 13,
            fontWeight: "600",
            color: img.bgColor,
          }}
        >
          {img.initials}
        </Text>
      </View>
    );
  }

  // Default: category icon
  return (
    <View
      style={{
        width: 36,
        height: 36,
        borderRadius: 10,
        backgroundColor: withAlpha(config.color, 0.08),
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      <Icon size={16} color={config.color} strokeWidth={1.5} />
    </View>
  );
}

export default function SearchResultRow({ item, index, onPress }: Props) {
  const capturePress = usePressCapture("SearchResultRow");
  return (
    <Animated.View entering={FadeIn.duration(200).delay(index * 30)}>
      <Pressable
        onPress={() => {
          capturePress(pressLabel(item.title), "search_result");
          onPress(item);
        }}
        style={({ pressed }) => ({
          flexDirection: "row",
          alignItems: "center",
          padding: 12,
          gap: 12,
          borderRadius: 12,
          backgroundColor: pressed
            ? semanticColors.overlay.pressed
            : "transparent",
        })}
      >
        <ResultIcon item={item} />

        <View style={{ flex: 1 }}>
          <Text
            className="font-dm-sans"
            style={{
              fontSize: 14,
              fontWeight: "500",
              color: EMBER.colors.text,
            }}
            numberOfLines={1}
          >
            {item.title}
          </Text>
          {item.subtitle ? (
            <Text
              className="font-dm-sans"
              style={{
                fontSize: 12,
                color: EMBER.colors.textLight,
                marginTop: 1,
              }}
              numberOfLines={1}
            >
              {item.subtitle}
            </Text>
          ) : null}
        </View>

        <ChevronRight
          size={14}
          color={EMBER.colors.textFaint}
          strokeWidth={1.5}
        />
      </Pressable>
    </Animated.View>
  );
}
